
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { motion } from "framer-motion";
import { useFriends } from "@/hooks/useFriends";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/hooks/useProfile";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMobileDetect } from "@/hooks/useMobileDetect";
import { useProfileSwitch } from "@/contexts/ProfileSwitchContext";

interface TimelineSwitcherProps {
  currentUsername?: string;
}

const TimelineSwitcher = ({ currentUsername }: TimelineSwitcherProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile();
  const { friends } = useFriends();
  const { additionalProfiles } = useProfileSwitch();
  const [activeTab, setActiveTab] = useState<string>("");
  const { isMobileOrTablet } = useMobileDetect();

  // Get current user's profile data
  const { data: currentUserProfile } = useQuery({
    queryKey: ['current-user-profile'],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('username, first_name, last_name, profile_picture')
        .eq('id', user.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id
  });

  // Set active tab based on current username
  useEffect(() => {
    if (currentUsername) {
      setActiveTab(currentUsername);
    } else if (currentUserProfile?.username) {
      setActiveTab(currentUserProfile.username);
    }
  }, [currentUsername, currentUserProfile]);

  const handleTabSwitch = (username: string) => {
    setActiveTab(username);
    navigate(`/timeline/${username}`);
  };

  const getDisplayName = (friend: any) => {
    const profile = friend.friend_profile;
    return profile.first_name && profile.last_name 
      ? `${profile.first_name} ${profile.last_name}`
      : profile.username;
  };

  const getInitials = (friend: any) => {
    const profile = friend.friend_profile;
    if (profile.first_name && profile.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase();
    }
    return profile.username.slice(0, 2).toUpperCase();
  };

  const getCurrentUserInitials = () => {
    if (currentUserProfile?.first_name && currentUserProfile?.last_name) {
      return `${currentUserProfile.first_name[0]}${currentUserProfile.last_name[0]}`.toUpperCase();
    }
    return currentUserProfile?.username?.slice(0, 2).toUpperCase() || "?";
  };

  const getCurrentUserDisplayName = () => {
    if (currentUserProfile?.first_name && currentUserProfile?.last_name) {
      return `${currentUserProfile.first_name} ${currentUserProfile.last_name}`;
    }
    return currentUserProfile?.username || "Me";
  };

  const getAdditionalProfileInitials = (profile: any) => {
    if (profile.first_name && profile.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase();
    }
    return profile.username?.slice(0, 2).toUpperCase() || "?";
  };

  const getAdditionalProfileDisplayName = (profile: any) => {
    const baseName = profile.first_name && profile.last_name
      ? `${profile.first_name} ${profile.last_name}`
      : profile.username;
    
    // Add gift emoji for gift relationship type
    return profile.relationship_type === 'gift' ? `🎁 ${baseName}` : baseName;
  };

  return (
    <div className="">
      <div className={`${isMobileOrTablet() ? 'w-full px-3' : 'container mx-auto px-4'} py-3`}>
        <div className={`flex items-center gap-2 overflow-x-auto scrollbar-hide min-w-0 ${isMobileOrTablet() ? 'scroll-smooth snap-x snap-mandatory' : ''}`}>
          {/* Current User Tab */}
          {currentUserProfile && (
            <motion.button
              onClick={() => handleTabSwitch(currentUserProfile.username)}
              className={`flex items-center gap-2 rounded-full transition-all whitespace-nowrap flex-shrink-0 min-w-fit ${
                isMobileOrTablet() 
                  ? 'px-3 py-2 snap-start min-h-[44px]' 
                  : 'px-4 py-2'
              } ${
                activeTab === currentUserProfile.username
                  ? 'bg-uplivion-blue text-white shadow-lg'
                  : 'bg-white/50 text-uplivion-blue hover:bg-white/70'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ width: 'auto', minWidth: 'fit-content' }}
            >
              <Avatar className={`${isMobileOrTablet() ? 'w-5 h-5' : 'w-6 h-6'} flex-shrink-0`}>
                <AvatarImage src={currentUserProfile.profile_picture || ""} />
                <AvatarFallback className={`${isMobileOrTablet() ? 'text-xs' : 'text-xs'} bg-uplivion-blue text-white`}>
                  {getCurrentUserInitials()}
                </AvatarFallback>
              </Avatar>
              <span className={`${isMobileOrTablet() ? 'text-xs' : 'text-sm'} font-medium flex-shrink-0`}>
                {isMobileOrTablet() ? (currentUserProfile.first_name || currentUserProfile.username) : getCurrentUserDisplayName()}
              </span>
            </motion.button>
          )}

          {/* Additional Profiles Tabs */}
          {additionalProfiles.map((profile) => (
            <motion.button
              key={profile.id}
              onClick={() => handleTabSwitch(profile.username)}
              className={`flex items-center gap-2 rounded-full transition-all whitespace-nowrap flex-shrink-0 min-w-fit ${
                isMobileOrTablet() 
                  ? 'px-3 py-2 snap-start min-h-[44px]' 
                  : 'px-4 py-2'
              } ${
                activeTab === profile.username
                  ? 'bg-uplivion-blue text-white shadow-lg'
                  : 'bg-white/50 text-uplivion-blue hover:bg-white/70'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ width: 'auto', minWidth: 'fit-content' }}
            >
              <Avatar className={`${isMobileOrTablet() ? 'w-5 h-5' : 'w-6 h-6'} flex-shrink-0`}>
                <AvatarImage src={profile.profile_picture || ""} />
                <AvatarFallback className={`${isMobileOrTablet() ? 'text-xs' : 'text-xs'} bg-uplivion-blue text-white`}>
                  {getAdditionalProfileInitials(profile)}
                </AvatarFallback>
              </Avatar>
              <span className={`${isMobileOrTablet() ? 'text-xs' : 'text-sm'} font-medium flex-shrink-0`}>
                {isMobileOrTablet() 
                  ? (profile.first_name || profile.username)
                  : getAdditionalProfileDisplayName(profile)
                }
              </span>
            </motion.button>
          ))}

          {/* Friends Tabs */}
          {friends.map((friend) => (
            <motion.button
              key={friend.id}
              onClick={() => handleTabSwitch(friend.friend_profile.username)}
              className={`flex items-center gap-2 rounded-full transition-all whitespace-nowrap flex-shrink-0 min-w-fit ${
                isMobileOrTablet() 
                  ? 'px-3 py-2 snap-start min-h-[44px]' 
                  : 'px-4 py-2'
              } ${
                activeTab === friend.friend_profile.username
                  ? 'bg-uplivion-blue text-white shadow-lg'
                  : 'bg-white/50 text-uplivion-blue hover:bg-white/70'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ width: 'auto', minWidth: 'fit-content' }}
            >
              <Avatar className={`${isMobileOrTablet() ? 'w-5 h-5' : 'w-6 h-6'} flex-shrink-0`}>
                <AvatarImage src={friend.friend_profile.profile_picture || ""} />
                <AvatarFallback className={`${isMobileOrTablet() ? 'text-xs' : 'text-xs'} bg-uplivion-blue text-white`}>
                  {getInitials(friend)}
                </AvatarFallback>
              </Avatar>
              <span className={`${isMobileOrTablet() ? 'text-xs' : 'text-sm'} font-medium flex-shrink-0`}>
                {isMobileOrTablet() 
                  ? (friend.friend_profile.first_name || friend.friend_profile.username)
                  : getDisplayName(friend)
                }
              </span>
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TimelineSwitcher;
